import { useState, useEffect, useRef } from 'react';
import { products, Product } from '@/mocks/products';

interface Relation {
  from: string;
  to: string;
  label: string;
}

const productRelations: Relation[] = [
  // 战略引擎 → 基础能力
  { from: 'strategy-2026', to: 'second-brain', label: '知识支撑' },
  { from: 'strategy-2026', to: 'task-board', label: '任务分解' },
  { from: 'strategy-2026', to: 'data-cockpit', label: '战略监控' },
  { from: 'org-structure', to: 'super-individual', label: '人才画像' },
  { from: 'org-structure', to: 'leo', label: '协同管理' },
  { from: 'org-structure', to: 'task-board', label: '任务协同' },
  { from: 'auaa', to: 'second-brain', label: '知识赋能' },
  { from: 'auaa', to: 'leo', label: 'AI驱动' },
  { from: 'auaa', to: 'official', label: '品牌联动' },
  { from: 'auaa', to: 'guiyu', label: '商业落地' },
  { from: 'auaa', to: 'rcu600-share', label: '产品推广' },
  { from: 'auaa', to: 'makeover', label: '资源整合' },
  { from: 'auaa', to: 'data-cockpit', label: '全局运营' },
  // 基础能力 → 品牌营销
  { from: 'second-brain', to: 'customer-service', label: '智能应答' },
  { from: 'second-brain', to: 'salesmind', label: '销售洞察' },
  { from: 'second-brain', to: 'traffic', label: '内容引擎' },
  { from: 'leo', to: 'customer-service', label: '客服增强' },
  { from: 'leo', to: 'salesmind', label: '销售AI' },
  { from: 'leo', to: 'official', label: '内容生成' },
  { from: 'super-individual', to: 'leo', label: 'AI赋能' },
  { from: 'task-board', to: 'data-cockpit', label: '效能监控' },
  { from: 'data-cockpit', to: 'second-brain', label: '数据回流' },
  { from: 'data-cockpit', to: 'leo', label: 'AI统计' },
  { from: 'data-cockpit', to: 'salesmind', label: '销售统计' },
  // 品牌营销 → 渔业领域
  { from: 'official', to: 'rcu600-share', label: '产品引流' },
  { from: 'official', to: 'yuce', label: '方案入口' },
  { from: 'traffic', to: 'official', label: '流量导入' },
  { from: 'traffic', to: 'guiyu', label: '获客转化' },
  { from: 'traffic', to: 'rcu600-share', label: '推广分发' },
  { from: 'customer-service', to: 'salesmind', label: '线索流转' },
  { from: 'salesmind', to: 'guiyu', label: '成交转化' },
  { from: 'salesmind', to: 'rcu600-share', label: '客户跟进' },
  { from: 'guiyu', to: 'yuce', label: '方案落地' },
  { from: 'makeover', to: 'yuce', label: '技改规划' },
  { from: 'makeover', to: 'aic', label: '系统集成' },
  // 渔业领域内部
  { from: 'rcu600-share', to: 'yuce', label: '验证规划' },
  { from: 'rcu600-share', to: 'rcu600-ai', label: 'AI接入' },
  { from: 'rcu600-share', to: 'rcu600-workbench', label: '协同启动' },
  { from: 'rcu600-ai', to: 'aic', label: '智能联动' },
  { from: 'rcu600-ai', to: 'fishview', label: '数据融合' },
  { from: 'rcu600-ai', to: 'water-monitor', label: '监测联动' },
  { from: 'rcu600-workbench', to: 'rcu600-ai', label: '工作协同' },
  { from: 'rcu600-workbench', to: 'task-board', label: '任务同步' },
  { from: 'yuce', to: 'aic', label: '规划执行' },
  { from: 'yuce', to: 'fishview', label: '数据验证' },
  { from: 'fishview', to: 'aic', label: '数据联动' },
  { from: 'fishview', to: 'vivian', label: '运维触发' },
  { from: 'fishview', to: 'water-monitor', label: '监测数据' },
  { from: 'vivian', to: 'pdm', label: '维护预警' },
  { from: 'aic', to: 'water-monitor', label: '监测控制' },
  { from: 'aic', to: 'pdm', label: '预测维护' },
  { from: 'water-monitor', to: 'fishview', label: '水质反馈' },
  { from: 'pdm', to: 'vivian', label: '故障工单' },
  // 跨层反馈
  { from: 'fishview', to: 'data-cockpit', label: '数据上报' },
  { from: 'aic', to: 'data-cockpit', label: 'AIC统计' },
  { from: 'customer-service', to: 'data-cockpit', label: '客服统计' },
];

const categoryConfig: Record<string, { name: string; color: string; icon: string; level: number }> = {
  strategic: { name: '战略引擎', color: '#FF6B35', icon: 'ri-rocket-2-line', level: 0 },
  foundation: { name: '基础能力', color: '#00D4FF', icon: 'ri-cpu-line', level: 1 },
  marketing: { name: '品牌营销', color: '#A855F7', icon: 'ri-megaphone-line', level: 2 },
  fishery: { name: '渔业领域', color: '#10B981', icon: 'ri-water-flash-line', level: 3 },
};

export default function ArchitectureGraph() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const getNodePositions = () => {
    const width = dimensions.width || 1200;
    const positions: Record<string, { x: number; y: number; product: Product }> = {};

    const strategicY = 60;
    const foundationY = 210;
    const marketingY = 380;
    const fisheryY = 570;

    const strategicProducts = products.filter((p) => p.category === 'strategic');
    const strategicSpacing = width / (strategicProducts.length + 1);
    strategicProducts.forEach((p, i) => {
      positions[p.id] = { x: strategicSpacing * (i + 1), y: strategicY, product: p };
    });

    const foundationProducts = products.filter((p) => p.category === 'foundation');
    const foundationSpacing = width / (foundationProducts.length + 1);
    foundationProducts.forEach((p, i) => {
      positions[p.id] = { x: foundationSpacing * (i + 1), y: foundationY, product: p };
    });

    const marketingProducts = products.filter((p) => p.category === 'marketing');
    const marketingSpacing = width / (marketingProducts.length + 1);
    marketingProducts.forEach((p, i) => {
      positions[p.id] = { x: marketingSpacing * (i + 1), y: marketingY, product: p };
    });

    const fisheryProducts = products.filter((p) => p.category === 'fishery');
    const fisherySpacing = width / (fisheryProducts.length + 1);
    fisheryProducts.forEach((p, i) => {
      positions[p.id] = { x: fisherySpacing * (i + 1), y: fisheryY, product: p };
    });

    return positions;
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const positions = getNodePositions();
    let animationFrame: number;
    let time = 0;

    const draw = () => {
      ctx.clearRect(0, 0, dimensions.width, dimensions.height);
      time += 0.02;

      productRelations.forEach((relation, index) => {
        const from = positions[relation.from];
        const to = positions[relation.to];
        if (!from || !to) return;

        const isHighlighted = hoveredNode === relation.from || hoveredNode === relation.to;
        const gradient = ctx.createLinearGradient(from.x, from.y, to.x, to.y);

        const fromColor = categoryConfig[from.product.category]?.color || '#00D4FF';
        const toColor = categoryConfig[to.product.category]?.color || '#00D4FF';

        if (isHighlighted) {
          gradient.addColorStop(0, fromColor);
          gradient.addColorStop(1, toColor);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 3;
          ctx.globalAlpha = 1;
        } else {
          gradient.addColorStop(0, `${fromColor}40`);
          gradient.addColorStop(1, `${toColor}40`);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 1.5;
          ctx.globalAlpha = 0.5;
        }

        ctx.beginPath();
        const midX = (from.x + to.x) / 2;
        const midY = (from.y + to.y) / 2 - 30;
        ctx.moveTo(from.x, from.y);
        ctx.quadraticCurveTo(midX, midY, to.x, to.y);
        ctx.stroke();

        if (isHighlighted) {
          const t = (Math.sin(time * 2 + index) + 1) / 2;
          const particleX = from.x + (to.x - from.x) * t;
          const particleY = from.y + (to.y - from.y) * t - 30 * Math.sin(Math.PI * t);

          ctx.beginPath();
          ctx.arc(particleX, particleY, 4, 0, Math.PI * 2);
          ctx.fillStyle = '#fff';
          ctx.globalAlpha = 1;
          ctx.fill();
        }

        ctx.globalAlpha = 1;
      });

      animationFrame = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animationFrame);
  }, [dimensions, hoveredNode]);

  const positions = getNodePositions();

  return (
    <div ref={containerRef} className="relative w-full h-[700px]">
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        className="absolute inset-0"
      />

      {Object.entries(positions).map(([id, pos]) => {
        const color = categoryConfig[pos.product.category]?.color || '#00D4FF';
        const isHovered = hoveredNode === id;

        return (
          <div
            key={id}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-300"
            style={{ left: pos.x, top: pos.y }}
            onMouseEnter={() => setHoveredNode(id)}
            onMouseLeave={() => setHoveredNode(null)}
            onClick={() => {
              if (pos.product.url) {
                window.open(pos.product.url, '_blank');
              }
            }}
          >
            <div
              className={`relative flex flex-col items-center transition-all duration-300 ${
                isHovered ? 'scale-125 z-10' : 'scale-100'
              }`}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300"
                style={{
                  backgroundColor: isHovered ? color : `${color}30`,
                  borderColor: color,
                  boxShadow: isHovered
                    ? `0 0 30px ${color}80`
                    : `0 0 15px ${color}40`,
                }}
              >
                <i
                  className={`${pos.product.icon} text-xl transition-colors duration-300`}
                  style={{ color: isHovered ? '#030712' : color }}
                ></i>
              </div>

              <div
                className={`mt-2 px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-300 ${
                  isHovered ? 'bg-white/20' : 'bg-white/5'
                }`}
                style={{
                  color: isHovered ? '#fff' : 'rgba(255,255,255,0.7)',
                }}
              >
                {pos.product.name.length > 6
                  ? pos.product.name.slice(0, 6) + '...'
                  : pos.product.name}
              </div>

              {isHovered && (
                <div
                  className="absolute top-full mt-4 p-4 rounded-xl border backdrop-blur-xl z-20 w-64"
                  style={{
                    backgroundColor: `${color}20`,
                    borderColor: `${color}50`,
                    boxShadow: `0 10px 40px ${color}30`,
                  }}
                >
                  <h4 className="text-sm font-bold text-white mb-1">
                    {pos.product.name}
                  </h4>
                  <p className="text-xs text-white/60 italic mb-3">
                    &ldquo;{pos.product.slogan}&rdquo;
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {pos.product.highlights.slice(0, 4).map((h, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-0.5 bg-white/10 rounded text-white/70"
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div className="absolute left-4 top-[60px] -translate-y-1/2">
        <span
          className="text-xs font-medium px-3 py-1.5 rounded-full"
          style={{ backgroundColor: '#FF6B3520', color: '#FF6B35' }}
        >
          战略引擎层
        </span>
      </div>
      <div className="absolute left-4 top-[210px] -translate-y-1/2">
        <span
          className="text-xs font-medium px-3 py-1.5 rounded-full"
          style={{ backgroundColor: '#00D4FF20', color: '#00D4FF' }}
        >
          基础能力层
        </span>
      </div>
      <div className="absolute left-4 top-[380px] -translate-y-1/2">
        <span
          className="text-xs font-medium px-3 py-1.5 rounded-full"
          style={{ backgroundColor: '#A855F720', color: '#A855F7' }}
        >
          品牌营销层
        </span>
      </div>
      <div className="absolute left-4 top-[570px] -translate-y-1/2">
        <span
          className="text-xs font-medium px-3 py-1.5 rounded-full"
          style={{ backgroundColor: '#10B98120', color: '#10B981' }}
        >
          渔业领域层
        </span>
      </div>
    </div>
  );
}